import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../integrations/supabase/client';
import { 
  ArrowLeft, 
  Bed, 
  Bath, 
  Car, 
  Ruler, 
  MapPin, 
  ExternalLink,
  Loader2,
  AlertTriangle,
  X,
  MessageCircle
} from 'lucide-react';

interface PropertyData {
  id: string;
  listing_id: string;
  title: string;
  description: string | null;
  virtual_tour_url: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  garage_quantity: number | null;
  area_built: number | null;
  list_price: number;
  rental_price: number | null;
  transaction_type: string;
  neighborhood: string;
  city: string;
  state: string;
  address: string;
}

const TourVirtualPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [property, setProperty] = useState<PropertyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadProperty = async () => {
      if (!id) {
        setError('ID do imóvel não fornecido');
        setIsLoading(false);
        return;
      }

      try {
        const { data, error: queryError } = await supabase
          .from('properties')
          .select('*')
          .eq('listing_id', id)
          .eq('is_active', true)
          .maybeSingle();

        if (queryError) throw queryError;

        if (!data) {
          setError('Imóvel não encontrado');
          return;
        }

        if (!data.virtual_tour_url) {
          setError('Este imóvel não possui tour virtual');
          return;
        }

        setProperty(data);
      } catch (err) {
        console.error('Erro ao carregar imóvel:', err);
        setError('Erro ao carregar dados do imóvel');
      } finally {
        setIsLoading(false);
      }
    };

    loadProperty();
  }, [id]);

  // SEO meta tags
  useEffect(() => {
    if (property) {
      document.title = `Tour Virtual 360° - ${property.title} - Fox Vendas`;
      
      // Open Graph tags para compartilhamento
      const metaTags = [
        { property: 'og:title', content: `Tour Virtual 360° - ${property.title}` },
        { property: 'og:description', content: `Faça um tour virtual 360° pelo imóvel: ${property.title} em ${property.neighborhood}, ${property.city}` },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: window.location.href }
      ];

      metaTags.forEach(({ property, content }) => {
        let meta = document.querySelector(`meta[property="${property}"]`);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute('property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      });
    }

    return () => {
      document.title = 'Fox Vendas - Imóveis';
    };
  }, [property]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getEmbedUrl = (url: string): string => {
    if (!url) return '';
    
    if (url.includes('matterport.com')) {
      if (url.includes('/show/')) {
        return url.includes('?') ? `${url}&play=1&qs=1` : `${url}?play=1&qs=1`;
      }
      const match = url.match(/matterport\.com\/(?:show\/\?m=|model\/)?([a-zA-Z0-9]+)/);
      if (match) {
        return `https://my.matterport.com/show/?m=${match[1]}&play=1&qs=1`;
      }
    }
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId[1]}?rel=0&autoplay=1`;
      }
    }
    
    return url;
  };

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!property) return;

    setIsSubmitting(true);
    try {
      await supabase.functions.invoke('receive-lead', {
        body: {
          name: contactForm.name,
          email: contactForm.email || undefined,
          phone: contactForm.phone || undefined,
          message: contactForm.message || `Interesse no imóvel via Tour Virtual: ${property.title}`,
          source: 'tour_virtual',
          property_id: property.listing_id,
          metadata: {
            property_title: property.title,
            from_page: 'TourVirtualPage'
          }
        }
      });

      alert('Obrigado! Entraremos em contato em breve.');
      setShowContactForm(false);
      setContactForm({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      console.error('Erro ao enviar lead:', err);
      alert('Erro ao enviar. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-white/70">Carregando tour virtual...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !property) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">{error || 'Imóvel não encontrado'}</h1>
          <p className="text-white/60 mb-6">O tour virtual que você procura não está disponível.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/busca')}
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Ver Imóveis
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-white/10 text-white px-6 py-3 rounded-lg hover:bg-white/20 transition-colors"
            >
              Página Inicial
            </button>
          </div>
        </div>
      </div>
    );
  }

  const price = property.transaction_type === 'rent' ? property.rental_price : property.list_price;
  const priceLabel = property.transaction_type === 'rent' ? '/mês' : '';

  return (
    <div className="min-h-screen bg-gray-900 relative">
      {/* Tour Virtual em Tela Cheia */}
      <iframe
        src={getEmbedUrl(property.virtual_tour_url!)}
        title={`Tour Virtual - ${property.title}`}
        className="absolute inset-0 w-full h-full border-0"
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; xr-spatial-tracking; vr"
      />

      {/* Header Overlay */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/70 via-black/40 to-transparent p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <button
            onClick={() => navigate(`/imovel/${property.listing_id}`)}
            className="flex items-center gap-2 text-white/90 hover:text-white transition-colors bg-black/30 backdrop-blur-sm px-3 py-2 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Ver imóvel completo</span>
          </button>
          
          <div className="flex items-center gap-2">
            <span className="text-white/70 text-sm hidden sm:inline">Tour Virtual 360°</span>
            <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-medium">
              {property.listing_id}
            </span>
          </div>
        </div>
      </div>

      {/* Footer Overlay com Info do Imóvel */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 sm:p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            {/* Info do Imóvel */}
            <div className="text-white">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2 line-clamp-2">
                {property.title}
              </h1>
              
              <div className="flex items-center gap-2 text-white/80 mb-3">
                <MapPin className="w-4 h-4" />
                <span className="text-sm sm:text-base">
                  {property.neighborhood}, {property.city} - {property.state}
                </span>
              </div>

              {/* Características */}
              <div className="flex flex-wrap gap-3 sm:gap-4 mb-3">
                {property.bedrooms && property.bedrooms > 0 && (
                  <div className="flex items-center gap-1.5 text-white/90">
                    <Bed className="w-4 h-4" />
                    <span className="text-sm">{property.bedrooms} quartos</span>
                  </div>
                )}
                {property.bathrooms && property.bathrooms > 0 && (
                  <div className="flex items-center gap-1.5 text-white/90">
                    <Bath className="w-4 h-4" />
                    <span className="text-sm">{property.bathrooms} banheiros</span>
                  </div>
                )}
                {property.garage_quantity && property.garage_quantity > 0 && (
                  <div className="flex items-center gap-1.5 text-white/90">
                    <Car className="w-4 h-4" />
                    <span className="text-sm">{property.garage_quantity} vagas</span>
                  </div>
                )}
                {property.area_built && property.area_built > 0 && (
                  <div className="flex items-center gap-1.5 text-white/90">
                    <Ruler className="w-4 h-4" />
                    <span className="text-sm">{property.area_built}m²</span>
                  </div>
                )}
              </div>

              {/* Preço */}
              <div className="text-2xl sm:text-3xl font-bold text-orange-400">
                {formatPrice(price || 0)}{priceLabel}
              </div>
            </div>

            {/* Botões de Ação */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => setShowContactForm(true)}
                className="flex items-center justify-center gap-2 bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium"
              >
                <MessageCircle className="w-5 h-5" />
                Tenho Interesse
              </button>
              
              <Link
                to={`/imovel/${property.listing_id}`}
                className="flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-lg hover:bg-white/30 transition-colors font-medium"
              >
                <ExternalLink className="w-5 h-5" />
                Ver Detalhes
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Contato */}
      {showContactForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-orange-500 text-white p-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Tenho Interesse</h2>
              <button
                onClick={() => setShowContactForm(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmitContact} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Seu nome completo"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone *
                </label>
                <input
                  type="tel"
                  required
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="(00) 00000-0000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="seu@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
                  placeholder="Gostaria de agendar uma visita..."
                />
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-orange-500 text-white py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  'Enviar'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourVirtualPage;
